import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Palette, UploadSimple, FloppyDisk, ArrowClockwise, ImageSquare, TrashSimple,
} from "@phosphor-icons/react";
import { useBranding } from "@/contexts/BrandingContext";
import BrandLogo from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSET_BASE = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => !u ? "" : (u.startsWith("http") ? u : `${ASSET_BASE}/${u.replace(/^\/+/, "")}`);

export default function BrandingManager() {
  const { branding, refresh } = useBranding();
  const [draft, setDraft] = useState({
    site_name: "", site_tagline: "", logo_url: "", favicon_url: "", primary_color: "#10b981",
    contact_email: "", contact_phone: "", contact_address: "",
    social_email: "", social_github: "", social_twitter: "", social_linkedin: "",
  });
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft({
      site_name: branding.site_name || "",
      site_tagline: branding.site_tagline || "",
      logo_url: branding.logo_url || "",
      favicon_url: branding.favicon_url || "",
      primary_color: branding.primary_color || "#10b981",
      contact_email: branding.contact_email || "",
      contact_phone: branding.contact_phone || "",
      contact_address: branding.contact_address || "",
      social_email: branding.social_email || "",
      social_github: branding.social_github || "",
      social_twitter: branding.social_twitter || "",
      social_linkedin: branding.social_linkedin || "",
    });
  }, [branding]);

  const save = useCallback(async () => {
    setSaving(true);
    setMsg("");
    try {
      await axios.put(`${API}/admin/branding`, draft, { withCredentials: true });
      await refresh();
      setMsg("Branding saved — new logo & name are live everywhere on the site.");
    } catch (e) { setMsg(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  }, [draft, refresh]);

  const uploadLogo = async (file) => {
    setMsg("Uploading logo…");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/branding/logo`, fd, {
        withCredentials: true,
      });
      setDraft((d) => ({ ...d, logo_url: r.data.logo_url }));
      await refresh();
      setMsg("Logo uploaded and live everywhere.");
    } catch (e) { setMsg(e.response?.data?.detail || "Upload failed"); }
  };

  const uploadFavicon = async (file) => {
    setMsg("Uploading favicon…");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/branding/favicon`, fd, {
        withCredentials: true,
      });
      setDraft((d) => ({ ...d, favicon_url: r.data.favicon_url }));
      await refresh();
      setMsg("Favicon uploaded.");
    } catch (e) { setMsg(e.response?.data?.detail || "Upload failed"); }
  };

  const clearLogo = async () => {
    if (!window.confirm("Remove the custom logo and revert to the default badge?")) return;
    setDraft((d) => ({ ...d, logo_url: "" }));
    await axios.put(`${API}/admin/branding`, { ...draft, logo_url: "" }, { withCredentials: true });
    await refresh();
    setMsg("Logo cleared — default badge is now in use.");
  };

  return (
    <div className="space-y-5" data-testid="branding-manager">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center shrink-0">
          <Palette size={18} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold text-white">Site Branding</h2>
          <p className="text-xs text-stone-400 mt-0.5">
            Change the site logo, name and browser-tab icon. Changes go live immediately across the navbar,
            footer, dashboard, login &amp; register pages, admin panel, and browser tab.
          </p>
        </div>
      </div>

      {msg && (
        <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-lg px-3 py-2 text-xs text-emerald-300"
             data-testid="branding-msg">{msg}</div>
      )}

      {/* Live preview */}
      <div className="bg-stone-900 rounded-xl border border-stone-800 p-5">
        <p className="text-[10px] text-stone-500 uppercase tracking-wider mb-3">Live preview</p>
        <div className="flex items-center gap-8 flex-wrap">
          <BrandLogo size={44} nameClassName="font-heading text-xl font-semibold text-white" />
          <BrandLogo size={28} iconSize={14} nameClassName="font-heading text-sm font-semibold text-white" />
          <BrandLogo size={64} iconSize={30} showName={false} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Logo */}
        <div className="bg-stone-900 rounded-xl border border-stone-800 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <ImageSquare size={16} weight="duotone" className="text-emerald-400" />
            <h3 className="font-heading text-sm font-semibold text-white">Logo</h3>
          </div>
          <div className="h-24 bg-stone-800/60 border border-stone-700/60 rounded-lg flex items-center justify-center">
            {draft.logo_url ? (
              <img src={resolveUrl(draft.logo_url)} alt="Current logo"
                   className="max-h-20 max-w-[200px] object-contain" />
            ) : (
              <span className="text-xs text-stone-500">No custom logo — using default green badge</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-2 rounded-lg inline-flex items-center gap-1.5 cursor-pointer"
                   data-testid="upload-logo-btn">
              <UploadSimple size={13} weight="bold" /> Upload logo
              <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
            </label>
            {draft.logo_url && (
              <button onClick={clearLogo}
                className="bg-red-600/20 hover:bg-red-600/30 text-red-300 text-xs px-3 py-2 rounded-lg inline-flex items-center gap-1.5"
                data-testid="clear-logo-btn">
                <TrashSimple size={12} /> Remove logo
              </button>
            )}
          </div>
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Or paste a logo URL</label>
            <input value={draft.logo_url} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })}
              placeholder="https://example.org/logo.svg"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500"
              data-testid="logo-url-input" />
          </div>
          <p className="text-[10px] text-stone-500">Recommended: transparent PNG or SVG, square, at least 128×128px.</p>
        </div>

        {/* Name + favicon + color */}
        <div className="bg-stone-900 rounded-xl border border-stone-800 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Palette size={16} weight="duotone" className="text-emerald-400" />
            <h3 className="font-heading text-sm font-semibold text-white">Name &amp; identity</h3>
          </div>
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Site name</label>
            <input value={draft.site_name} onChange={(e) => setDraft({ ...draft, site_name: e.target.value })}
              placeholder="Conexus Foundation"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
              data-testid="site-name-input" />
          </div>
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Site tagline (used in browser tab)</label>
            <input value={draft.site_tagline} onChange={(e) => setDraft({ ...draft, site_tagline: e.target.value })}
              placeholder="Earth Monitoring"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
              data-testid="site-tagline-input" />
          </div>
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Favicon (browser tab icon)</label>
            <div className="flex items-center gap-2">
              {draft.favicon_url && (
                <img src={resolveUrl(draft.favicon_url)} alt="favicon"
                     className="w-8 h-8 object-contain bg-stone-800 border border-stone-700 rounded" />
              )}
              <label className="bg-stone-800 hover:bg-stone-700 text-stone-200 text-xs px-3 py-2 rounded-lg inline-flex items-center gap-1.5 cursor-pointer"
                     data-testid="upload-favicon-btn">
                <UploadSimple size={13} /> Upload
                <input type="file" accept="image/png,image/x-icon,image/svg+xml" className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadFavicon(e.target.files[0])} />
              </label>
              <input value={draft.favicon_url} onChange={(e) => setDraft({ ...draft, favicon_url: e.target.value })}
                placeholder="Or paste URL"
                className="flex-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={refresh}
          className="bg-stone-800 hover:bg-stone-700 text-stone-300 text-xs px-4 py-2 rounded-lg inline-flex items-center gap-1.5"
          data-testid="branding-reload-btn">
          <ArrowClockwise size={13} /> Reload
        </button>
        <button onClick={save} disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-lg inline-flex items-center gap-2"
          data-testid="save-branding-btn">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Contact info shown on home page */}
      <div className="bg-stone-900 rounded-xl border border-stone-800 p-5 space-y-3">
        <h3 className="font-heading text-sm font-semibold text-white">Contact info (home page)</h3>
        <p className="text-[10px] text-stone-500">These fields drive the Contact section on the home page and the Contact Us page.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Email</label>
            <input value={draft.contact_email} onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
              placeholder="info@conexusfoundation.org" data-testid="brand-contact-email"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Phone</label>
            <input value={draft.contact_phone} onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
              placeholder="+1 555 123 4567" data-testid="brand-contact-phone"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Address</label>
            <input value={draft.contact_address} onChange={(e) => setDraft({ ...draft, contact_address: e.target.value })}
              placeholder="Accra, Ghana" data-testid="brand-contact-address"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500" />
          </div>
        </div>
      </div>

      {/* Social icon links */}
      <div className="bg-stone-900 rounded-xl border border-stone-800 p-5 space-y-3">
        <h3 className="font-heading text-sm font-semibold text-white">Social links (footer icons)</h3>
        <p className="text-[10px] text-stone-500">Empty fields hide the corresponding icon in the footer.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Email (for envelope icon)</label>
            <input value={draft.social_email} onChange={(e) => setDraft({ ...draft, social_email: e.target.value })}
              placeholder="info@conexusfoundation.org" data-testid="brand-social-email"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">GitHub URL</label>
            <input value={draft.social_github} onChange={(e) => setDraft({ ...draft, social_github: e.target.value })}
              placeholder="https://github.com/your-org" data-testid="brand-social-github"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">Twitter / X URL</label>
            <input value={draft.social_twitter} onChange={(e) => setDraft({ ...draft, social_twitter: e.target.value })}
              placeholder="https://x.com/your-handle" data-testid="brand-social-twitter"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="text-[10px] text-stone-500 uppercase tracking-wider block mb-1">LinkedIn URL</label>
            <input value={draft.social_linkedin} onChange={(e) => setDraft({ ...draft, social_linkedin: e.target.value })}
              placeholder="https://linkedin.com/company/…" data-testid="brand-social-linkedin"
              className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500" />
          </div>
        </div>
      </div>
    </div>
  );
}
